// bb-marquee.js — Boosted Brat compliance ticker
//
// Custom element port of what used to be an inline IIFE. Why a custom element:
// Shopify's Section Rendering API (the theme editor uses it on every re-render)
// injects markup via innerHTML, and inline <script> tags inserted that way do
// NOT execute — so the old inline IIFE never ran in the editor and the ticker
// sat dead. A custom element upgrades automatically whenever the browser parses
// or inserts <bb-marquee-el>, including on every editor re-render, so the logic
// runs reliably without depending on a full page load.
//
// The measuring/cloning/hand-off logic is unchanged from the inline version; it
// is only rescoped from a document.getElementById(root) to `this`. JS measures
// and clones only, then hands off to the compositor-run CSS keyframe
// (bb-marquee-scroll). Reduced motion stops the scroll entirely and skips
// cloning; the static wrapped list is the pre-JS / no-JS / reduced-motion state.

class BBMarquee extends HTMLElement {
  connectedCallback() {
    // Double-init guard. Also lets a reconnect (editor re-render moves the same
    // node, or it is detached then re-attached) rebind fresh listeners below.
    if (this._bbInit) return;
    this._bbInit = true;

    this._viewport = this.querySelector('[data-bb-viewport]');
    this._track = this.querySelector('[data-bb-track]');
    this._group = this.querySelector('[data-bb-group]');
    if (!this._viewport || !this._track || !this._group) return;

    var speed = parseFloat(this.dataset.speed) || 40; // pixels per second
    if (speed < 5) speed = 5;
    this._speed = speed;

    this._motionQuery = window.matchMedia
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : null;

    // Bound handlers kept as instance fields so disconnectedCallback can remove
    // exactly what connectedCallback added.
    this._build = this.build.bind(this);
    this._schedule = this.schedule.bind(this);
    this._pending = null;

    // Rebuild on width change. Clones are siblings of the measured group and do
    // not alter either observed box, so this cannot feed itself.
    if (window.ResizeObserver) {
      this._observer = new ResizeObserver(this._schedule);
      this._observer.observe(this._viewport);
      this._observer.observe(this._group);
    } else {
      window.addEventListener('resize', this._schedule);
    }

    if (this._motionQuery) {
      // Re-run if the OS reduced-motion setting flips mid-session.
      if (this._motionQuery.addEventListener) {
        this._motionQuery.addEventListener('change', this._build);
      } else if (this._motionQuery.addListener) {
        this._motionQuery.addListener(this._build);
      }
    }

    this.build();

    // A late web font changes the measured width, which would put the seam in
    // the wrong place. Measure again once fonts have settled.
    if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
      document.fonts.ready.then(this._build).catch(function () {});
    }
  }

  disconnectedCallback() {
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    } else {
      window.removeEventListener('resize', this._schedule);
    }

    if (this._motionQuery) {
      if (this._motionQuery.removeEventListener) {
        this._motionQuery.removeEventListener('change', this._build);
      } else if (this._motionQuery.removeListener) {
        this._motionQuery.removeListener(this._build);
      }
    }

    clearTimeout(this._pending);

    // Clear appended clones and the ready flag so a reconnect starts clean and
    // does not accumulate duplicate copies on editor re-render.
    this.teardown();

    // Allow connectedCallback to fully re-initialise if this node is reattached.
    this._bbInit = false;
  }

  teardown() {
    if (!this._track) return;
    var clones = this._track.querySelectorAll('[data-bb-clone]');
    for (var i = 0; i < clones.length; i++) {
      clones[i].parentNode.removeChild(clones[i]);
    }
    this.removeAttribute('data-bb-ready');
    this.style.removeProperty('--bb-marquee-shift');
    this.style.removeProperty('--bb-marquee-duration');
  }

  build() {
    // Always clear previously-appended clones first, so repeated builds (resize,
    // font settle, motion flip, reconnect) never accumulate duplicates.
    this.teardown();

    // Reduced motion: no clones, no animation, no ready flag. The CSS static
    // wrapped list is already what is on screen, so there is nothing to do.
    if (this._motionQuery && this._motionQuery.matches) return;

    var shift = this._group.getBoundingClientRect().width;
    var viewportWidth = this._viewport.getBoundingClientRect().width;
    if (!shift || !viewportWidth) return;

    // Overflow the viewport by at least 2x, plus the one copy the animation
    // travels through before it resets.
    var copies = Math.ceil((viewportWidth * 2) / shift) + 1;
    if (copies < 2) copies = 2;

    var fragment = document.createDocumentFragment();
    for (var i = 0; i < copies; i++) {
      var clone = this._group.cloneNode(true);
      clone.removeAttribute('data-bb-group');
      clone.setAttribute('data-bb-clone', '');
      // Announced once, by the original. Clones are pure decoration.
      clone.setAttribute('aria-hidden', 'true');
      var links = clone.querySelectorAll('a');
      for (var j = 0; j < links.length; j++) {
        links[j].setAttribute('tabindex', '-1');
      }
      fragment.appendChild(clone);
    }
    this._track.appendChild(fragment);

    this.style.setProperty('--bb-marquee-shift', shift.toFixed(2) + 'px');
    this.style.setProperty('--bb-marquee-duration', Math.max(1, shift / this._speed).toFixed(2) + 's');
    this.setAttribute('data-bb-ready', '1');
  }

  schedule() {
    clearTimeout(this._pending);
    this._pending = setTimeout(this._build, 150);
  }
}

if (!customElements.get('bb-marquee-el')) {
  customElements.define('bb-marquee-el', BBMarquee);
}
